/**
 * AlgoBuilderX Schema Mapper
 *
 * Transforms backend API responses to frontend-friendly format.
 *
 * Backend → Frontend field mapping:
 * - componentId → componentId (same)
 * - name → name (same)
 * - description → description + tooltip
 * - type → category (lowercase)
 * - parameters[].key → parameters[].name
 * - parameters[].displayName → parameters[].label
 * - parameters[].dataType → parameters[].type
 * - parameters[].isRequired → parameters[].required
 * - parameters[].description → parameters[].tooltip
 * - parameters[].minValue → parameters[].min
 * - parameters[].maxValue → parameters[].max
 */

// ═══════════════════════════════════════════════════════════════════════════════
// TYPE MAPPINGS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Backend component type enum → frontend category string
 * 0 = Indicator, 1 = EntryRule, 2 = ExitRule, 3 = LotSizer, 4 = RiskManager
 */
const COMPONENT_TYPE_MAP = {
  0: 'indicator',
  1: 'entryrule',
  2: 'exitrule',
  3: 'lotsizer',
  4: 'riskmanager'
};

/**
 * Backend dataType enum → frontend input type
 * 0 = Integer, 1 = Decimal, 2 = String, 3 = Boolean, 4 = List
 */
const DATA_TYPE_MAP = {
  0: 'number',    // Integer
  1: 'number',    // Decimal
  2: 'text',      // String
  3: 'checkbox',  // Boolean
  4: 'select',    // List
  // Also support string keys for backwards compatibility
  'String': 'text',
  'Integer': 'number',
  'Decimal': 'number',
  'Boolean': 'checkbox',
  'List': 'select'
};

/**
 * Map backend component type to frontend category
 */
const mapComponentType = (type) => {
  if (typeof type === 'number') {
    return COMPONENT_TYPE_MAP[type] || 'unknown';
  }
  // Fallback for string type
  return String(type).toLowerCase().replace(/\s+/g, '') || 'unknown';
};

/**
 * Map backend dataType to frontend type
 * Special case: if key contains "symbol" → "symbol" type
 */
const mapDataType = (dataType, key) => {
  if (key && key.toLowerCase().includes('symbol')) {
    return 'symbol';
  }
  return DATA_TYPE_MAP[dataType] || 'text';
};

// ═══════════════════════════════════════════════════════════════════════════════
// PARAMETER MAPPING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Transform a single parameter from backend to frontend format
 */
export const mapParameter = (param) => ({
  name: param.key,
  label: param.displayName,
  type: mapDataType(param.dataType, param.key),
  defaultValue: param.defaultValue,
  required: param.isRequired,
  tooltip: param.description,
  min: param.minValue,
  max: param.maxValue,
  // Preserve original for reference
  _original: param
});

/**
 * Transform array of parameters
 */
export const mapParameters = (parameters) => {
  if (!parameters || !Array.isArray(parameters)) return [];
  return parameters.map(mapParameter);
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT MAPPING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Transform a single component from backend to frontend format
 */
export const mapComponent = (component) => ({
  componentId: component.componentId,
  name: component.name,
  description: component.description,
  tooltip: component.description, // Use description as tooltip
  category: mapComponentType(component.type),
  author: component.author,
  version: component.version,
  isValidated: component.isValidated,
  tags: component.tags || [],
  parameters: mapParameters(component.parameters),
  // Preserve original for reference
  _original: component
});

/**
 * Transform array of components
 */
export const mapComponents = (components) => {
  if (!components || !Array.isArray(components)) return [];
  return components.map(mapComponent);
};

// ═══════════════════════════════════════════════════════════════════════════════
// GROUPING UTILITIES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Group components by category (type)
 * @param {Array} components - Array of mapped components
 * @returns {Object} { indicator: [], entryrule: [], exitrule: [], lotsizer: [], riskmanager: [] }
 */
export const groupComponentsByCategory = (components) => {
  const grouped = {
    indicator: [],
    entryrule: [],
    exitrule: [],
    lotsizer: [],
    riskmanager: []
  };

  components.forEach(component => {
    const category = component.category || 'unknown';
    if (grouped[category]) {
      grouped[category].push(component);
    } else {
      console.warn(`[AlgoBuilderX] Unknown component category: ${category}`, component);
    }
  });

  console.log('[AlgoBuilderX] Grouped components:', {
    indicators: grouped.indicator.length,
    entryRules: grouped.entryrule.length,
    exitRules: grouped.exitrule.length,
    lotSizers: grouped.lotsizer.length,
    riskManagers: grouped.riskmanager.length
  });

  return grouped;
};

// ═══════════════════════════════════════════════════════════════════════════════
// REVERSE MAPPING (Frontend → Backend)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Transform frontend parameter values back to backend format
 */
export const unmapParameterValue = (value, type) => {
  switch (type) {
    case 'number':
      return parseFloat(value) || 0;
    case 'checkbox':
      return Boolean(value);
    default:
      return value;
  }
};

/**
 * Build component config for backend from frontend selections
 */
export const buildComponentConfig = (componentId, parameters, parameterValues) => {
  const config = {
    componentId,
    parameters: {}
  };

  parameters.forEach(param => {
    const value = parameterValues[param.name];
    if (value !== undefined && value !== null && value !== '') {
      config.parameters[param.name] = unmapParameterValue(value, param.type);
    } else if (param.defaultValue !== undefined && param.defaultValue !== null) {
      config.parameters[param.name] = param.defaultValue;
    }
  });

  return config;
};

/**
 * Extract symbols from indicator parameters
 */
const extractSymbolsFromIndicators = (indicators) => {
  const symbols = new Set();

  if (!indicators || indicators.length === 0) return [];

  indicators.forEach(ind => {
    const values = ind.values || {};
    // Check common symbol parameter names
    Object.entries(values).forEach(([key, value]) => {
      if (key.toLowerCase().includes('symbol') && value) {
        symbols.add(value);
      }
    });
  });

  return Array.from(symbols);
};

/**
 * Build full strategy config for backend
 */
export const buildStrategyConfig = (formData, accountNumber) => {
  const symbols = formData.selectedSymbols && formData.selectedSymbols.length > 0
    ? formData.selectedSymbols
    : extractSymbolsFromIndicators(formData.indicators);

  const config = {
    name: formData.name,
    description: formData.description || '',
    symbols: symbols,
    accountNumber: accountNumber ? Number(accountNumber) : null,
    isEnabled: formData.isEnabled !== false,
    components: {
      indicators: [],
      entryRules: [],
      exitRules: [],
      lotSizer: null,
      riskManagers: []
    }
  };

  // Indicators (array)
  if (formData.indicators && formData.indicators.length > 0) {
    config.components.indicators = formData.indicators.map(ind =>
      buildComponentConfig(ind.componentId, ind.component?.parameters || ind.parameters || [], ind.values || {})
    );
  }

  // Entry rule (array)
  if (formData.entryRule) {
    config.components.entryRules = [
      buildComponentConfig(
        formData.entryRule.componentId,
        formData.entryRule.component?.parameters || formData.entryRule.parameters || [],
        formData.entryRule.values || {}
      )
    ];
  }

  // Exit rule (array)
  if (formData.exitRule) {
    config.components.exitRules = [
      buildComponentConfig(
        formData.exitRule.componentId,
        formData.exitRule.component?.parameters || formData.exitRule.parameters || [],
        formData.exitRule.values || {}
      )
    ];
  }

  // Lot sizer (single object)
  if (formData.lotSizer) {
    config.components.lotSizer = buildComponentConfig(
      formData.lotSizer.componentId,
      formData.lotSizer.component?.parameters || formData.lotSizer.parameters || [],
      formData.lotSizer.values || {}
    );
  }

  // Risk manager (array, optional)
  if (formData.riskManager) {
    config.components.riskManagers = [
      buildComponentConfig(
        formData.riskManager.componentId,
        formData.riskManager.component?.parameters || formData.riskManager.parameters || [],
        formData.riskManager.values || {}
      )
    ];
  }

  console.log('[AlgoBuilderX] Built strategy config:', JSON.stringify(config, null, 2));

  return config;
};

export default {
  mapParameter,
  mapParameters,
  mapComponent,
  mapComponents,
  groupComponentsByCategory,
  unmapParameterValue,
  buildComponentConfig,
  buildStrategyConfig
};
